import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
    Box,
    IconButton,
    Slider,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import React, { useEffect, useState } from 'react';
import ExportWindow from './export/ExportWindow';
import SliderNumeric from './sliderNumeric';
import SliderDualNumeric from './sliderDualNumeric';
import SliderSingle from './sliderSingle';
import PanelSlider from './panelSlider';
import { translate } from '../constants';


const CustomModal = ({
    open,
    onClose,
    color,
    title,
    type,  // 'slider' or 'search' or 'views' or 'create' or 'warning'
    // Slider specific props
    value,
    onChange,
    onChangeCommitted,
    min,
    max,
    precision = false,
    step = 1,
    unit = '',
    isSingleValue = false, // New prop to indicate single value slider
    defaultValue = 250, // Default value for reset button
    // Search specific props
    onSearchChange,
    searchValue,
    searchContext = 0,
    onSearchContextChange = null,
    store,
    // Views specific props
    options = [],
    // Create specific props
    selectedOptions = [],
    onOptionChange,
    // Warning specific props
    message = '',
    onConfirm, // New prop for confirmation callback
    // Media creation props
    onCreateMedia,
    // Export title default
    defaultExportTitle = '',
    // Selected clip duration in ms (for export dialog)
    selectedDurationMs = 0,
    // Transcript style for caption preview
    transcriptStyle = null,
    // First ticked clip's poster + words, for the export window's live preview
    exportPreview = null,
    // Export window: copy/share the spreadsheet link instead of making a file
    onShareLink = null,
    onEditText = null,
}) => {
    const theme = useTheme();
    const [initialRange] = useState({ min, max });
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const wide = (initialRange.max - initialRange.min);


    const handleMouseDown = (e) => {
        if (e.target.closest('.modal-close-btn')) return; // Ignore if clicking close button
        setIsDragging(true);
        setDragStart({
            x: e.clientX - position.x,
            y: e.clientY - position.y
        });
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;

        setPosition({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragStart]);

    // Handler for the clear button
    const handleClearSearch = () => {
        // Mimic the event structure expected by onSearchChange
        onSearchChange({ target: { value: '' } });
    };

    // Export has its own window (grid/export/ExportWindow.jsx): format cards + a live preview.
    if (type === 'create') {
        return (
            <ExportWindow
                open={open}
                onClose={onClose}
                options={options}
                selectedOptions={selectedOptions}
                onOptionChange={onOptionChange}
                onCreateMedia={onCreateMedia}
                defaultExportTitle={defaultExportTitle}
                selectedDurationMs={selectedDurationMs}
                preview={exportPreview || {}}
                onShareLink={onShareLink}
                onEditText={onEditText}
            />
        );
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="custom-modal"
            BackdropProps={{
                sx: { backgroundColor: 'transparent' },
                onClick: (e) => {
                    if (e.target === e.currentTarget) onClose();
                }
            }}
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
            disableScrollLock
            slotProps={{
                backdrop: {
                    timeout: 0,
                }
            }}
            style={{ pointerEvents: 'none' }}
        >
            <Paper
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
                    width: {
                        xs: type === 'search' ? '90vw' :
                            type === 'slider' ? '90vw' :
                                type === 'views' ? '80vw' :
                                    type === 'create' ? '90vw' :
                                        type === 'warning' ? '90vw' : '70vw',
                        sm: type === 'search' ? 400 :
                            type === 'slider' ? Math.min(Math.max(400, Math.max(600, wide)), '80vw') :
                                type === 'views' ? 300 :
                                    type === 'create' ? 400 :
                                        type === 'warning' ? 400 : 200,
                        md: type === 'search' ? 400 :
                            type === 'slider' ? Math.max(400, Math.max(600, wide)) :
                                type === 'views' ? 300 :
                                    type === 'create' ? 400 :
                                        type === 'warning' ? 400 : 200
                    },
                    maxWidth: '95vw',
                    minHeight: type === 'slider' && precision ? 280 : 'auto',
                    bgcolor: 'transparent',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    borderRadius: 2,
                    // Near-solid on phones so the dialog reads as its own surface; the airy
                    // translucent look stays on desktop where the ET behind it is useful context.
                    backgroundColor: {
                        xs: theme.palette.mode === 'dark'
                            ? 'rgba(28, 26, 33, 0.98)'
                            : 'rgba(248, 248, 248, 0.99)',
                        sm: theme.palette.mode === 'dark'
                            ? 'rgba(40, 40, 40, 0.5)'
                            : 'rgba(240, 240, 240, 0.5)',
                    },
                    backdropFilter: 'blur(8px)',
                    border: `1px solid ${color}30`,
                    overflow: 'hidden',
                    pointerEvents: 'auto',
                    userSelect: 'none',
                    // The create/export variant can outgrow a phone screen; cap the height and
                    // scroll the body so the ✕ and OK button are always reachable.
                    maxHeight: '85vh',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                    <Box
                        onMouseDown={handleMouseDown}
                        sx={{
                            flexShrink: 0,
                            height: '40px',
                            cursor: 'move',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.03)'
                                : 'rgba(0, 0, 0, 0.03)',
                            borderTopLeftRadius: 'inherit',
                            borderTopRightRadius: 'inherit',
                            '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : 'rgba(0, 0, 0, 0.05)',
                            }
                        }}
                    >
                        {type !== 'warning' && (
                            <Typography variant="subtitle1" sx={{
                                color: color,
                                fontWeight: 500,
                                textAlign: 'center',
                                opacity: 0.9,
                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                px: { xs: 1, sm: 0 }
                            }}>
                                {title}
                            </Typography>
                        )}
                    </Box>

                    <IconButton
                        className="modal-close-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClose();
                        }}
                        size="small"
                        sx={{
                            position: 'absolute',
                            right: 4,
                            top: 8,
                            padding: '10px', // generous hit target — the tiny × was hard to hit in the panel
                            color: theme.palette.text.secondary,
                            opacity: 0.6,
                            zIndex: 1,
                            '&:hover': {
                                color: color,
                                opacity: 1,
                                backgroundColor: `${color}10`
                            }
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>

                    <Box sx={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0, WebkitOverflowScrolling: 'touch', p: { xs: 1.5, sm: 2 } }}>
                    <Stack spacing={2} sx={{ pt: 0.5 }}>
                        <Box sx={{ px: { xs: 1, sm: 2 } }}>
                            {type === 'warning' ? (
                                <>
                                    <Alert
                                        severity="warning"
                                        sx={{
                                            alignItems: 'center',
                                            '& .MuiAlert-message': {
                                                width: '100%',
                                                textAlign: 'center'
                                            }
                                        }}
                                    >
                                        {message}
                                    </Alert>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                        <Tooltip title="Confirm">
                                            <Button
                                                onClick={() => onConfirm(true)}
                                                variant="contained"
                                                color="success"
                                                size="small"
                                                sx={{
                                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                    px: { xs: 2, sm: 3 }
                                                }}
                                            >
                                                Yes
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title="Cancel">
                                            <Button
                                                onClick={() => onConfirm(false)}
                                                variant="contained"
                                                color="error"
                                                size="small"
                                                sx={{
                                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                    px: { xs: 2, sm: 3 },
                                                    ml: 1
                                                }}
                                            >
                                                No
                                            </Button>
                                        </Tooltip>
                                    </Box>
                                </>
                            ) : type === 'slider' ? (
                                <>
                                    {isSingleValue ? (
                                        <>
                                            <SliderSingle
                                                min={min}
                                                max={max}
                                                label={title.replace(/\s*\([^)]*\)/, '')} // Remove unit from title for label
                                                color={color === theme.palette.success.main ? 'success' :
                                                    color === theme.palette.warning.main ? 'warning' :
                                                        color === theme.palette.primary.main ? 'primary' :
                                                            'primary'}
                                                callback={(newValue) => {
                                                    onChange(null, newValue);
                                                }}
                                                reset={true}
                                                initialValue={Array.isArray(value) ? value[0] : value}
                                                step={step}
                                                unit={unit}
                                            />
                                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                                <Tooltip title="Reset to default">
                                                    <IconButton
                                                        onClick={() => onChange(null, defaultValue)} // Responsive default video width
                                                        size="small"
                                                        sx={{
                                                            color: theme.palette.error.main,
                                                        }}
                                                    >
                                                        <RestartAltIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </>
                                    ) : precision ? (
                                        <>
                                            <SliderDualNumeric
                                                textLen={[initialRange.min, initialRange.max]}
                                                label=""
                                                color={color === theme.palette.success.main ? 'success' :
                                                    color === theme.palette.warning.main ? 'warning' :
                                                        'primary'}
                                                callback={(newValues) => {
                                                    onChange(null, newValues);
                                                }}
                                                reset={true}
                                                initialValue={value}
                                            />
                                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                                <Tooltip title="Reset to initial value">
                                                    <IconButton
                                                        onClick={() => onChange(null, [initialRange.min, initialRange.max])}
                                                        size="small"
                                                        sx={{
                                                            color: theme.palette.error.main,
                                                        }}
                                                    >
                                                        <RestartAltIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </>
                                    ) : (
                                        <>
                                            <SliderNumeric
                                                textLen={[initialRange.min, initialRange.max]}
                                                label=""
                                                color={color === theme.palette.success.main ? 'success' :
                                                    color === theme.palette.warning.main ? 'warning' :
                                                        'primary'}
                                                callback={(newValues) => {
                                                    onChange(null, newValues);
                                                }}
                                                reset={true}
                                                initialValue={value}
                                            />
                                            <Typography variant="caption" sx={{
                                                color: theme.palette.text.secondary,
                                                textAlign: 'center',
                                                display: 'block',
                                                mb: 1,
                                                fontSize: { xs: '0.75rem', sm: '0.8rem' }
                                            }}>
                                            </Typography>
                                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                                <Tooltip title="Reset to full range">
                                                    <IconButton
                                                        onClick={() => onChange(null, [initialRange.min, initialRange.max])}
                                                        size="small"
                                                        sx={{
                                                            color: theme.palette.error.main,
                                                        }}
                                                    >
                                                        <RestartAltIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </>
                                    )}
                                </>
                            ) : type === 'search' ? (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <TextField
                                            autoFocus
                                            fullWidth
                                            value={searchValue || ''}
                                            onChange={onSearchChange}
                                            placeholder="Search..."
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: `${color}40`,
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: `${color}80`,
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: color,
                                                    },
                                                },
                                            }}
                                            InputProps={{
                                                endAdornment: searchValue ? (
                                                    <InputAdornment position="end">
                                                        <Tooltip title="Clear search">
                                                            <IconButton
                                                                edge="end"
                                                                onClick={handleClearSearch}
                                                                size="small"
                                                                sx={{ color: `${color}80` }}
                                                            >
                                                                <RestartAltIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                ) : null,
                                            }}
                                        />
                                    </Box>
                                    {onSearchContextChange && (
                                        <Box sx={{ px: 0.5, pb: 0.5 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.25 }}>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    Context around matches
                                                </Typography>
                                                <Typography variant="caption" sx={{ color, fontWeight: 700, fontSize: '11px' }}>
                                                    {searchContext === 0 ? 'just the match' : `${searchContext} before & after`}
                                                </Typography>
                                            </Box>
                                            <PanelSlider
                                                value={searchContext}
                                                onChange={(_, v) => onSearchContextChange && onSearchContextChange(v)}
                                                step={1} min={0} max={5} marks size="small"
                                                sx={{ color, '& .MuiSlider-markLabel': { fontSize: '10px' } }}
                                            />
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px', display: 'block', mt: -0.25, lineHeight: 1.3 }}>
                                                Reveal the clips around each match — for search AND the AI / Filter-By filters. They show dimmed but you can still select them. Your selection is never touched.
                                            </Typography>
                                        </Box>
                                    )}
                                </>
                            ) : type === 'views' ? (
                                <FormControl>
                                    <RadioGroup
                                        value={value}
                                        onChange={onChange}
                                    >
                                        {options.map((option) => (
                                            <FormControlLabel
                                                key={option}
                                                value={option}
                                                control={<Radio size="small" sx={{ color: color }} />}
                                                label={<Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{translate(option)}</Typography>}
                                            />
                                        ))}
                                    </RadioGroup>
                                </FormControl>
                            ) : null}
                        </Box>
                    </Stack>
                </Box>
            </Paper>
        </Modal>
    );
};


export default CustomModal;
